import React, { useContext, useEffect, useMemo, useState } from 'react'
import api from '../../services/api'
import '../../css/tickets.css'
import { AuthContext } from '../../contexts/AuthContext'

export default function TicketCreate({ onCreated }) {
  const { user, hasPermission } = useContext(AuthContext)

  // Permessi: identico al tuo, lasciato invariato per non toccare la logica
  const isSupport = useMemo(() => {
    if (!user) return false
    const roles = user.roles || []
    if (roles.some(r => r.name === 'admin' || r.name === 'ticket_support')) return true
    return hasPermission('tickets_categories_manage')
  }, [user, hasPermission])

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')
  const [categories, setCategories] = useState([])
  const [categoryId, setCategoryId] = useState('')
  const [newCategory, setNewCategory] = useState('')
  const [showNewCat, setShowNewCat]   = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState({ subject: false, message: false })

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const { data } = await api.get('/ticket-categories')
        if (alive) setCategories(data)
      } catch {
        if (alive) setError('Errore nel caricamento categorie')
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => { alive = false }
  }, [])

  // Se l’utente apre “Nuova categoria”, svuoto la select
  useEffect(() => {
    if (showNewCat) setCategoryId('')
    if (!showNewCat) setNewCategory('')
  }, [showNewCat])

  const submit = async (e) => {
    e.preventDefault()
    const hasSubject = subject.trim().length > 0
    const hasMessage = message.trim().length > 0
    if (!hasSubject || !hasMessage) {
      setTouched({ subject: true, message: true })
      setError('Compila i campi obbligatori evidenziati')
      return
    }

    setError('')
    setSaving(true)
    try {
      const payload = {
        subject: subject.trim(),
        message: message.trim(),
      }
      if (isSupport && newCategory.trim()) payload.newCategory = newCategory.trim()
      else if (categoryId)                 payload.categoryId  = Number(categoryId)

      await api.post('/tickets', payload)

      // reset “gentile”
      setSubject(''); setMessage(''); setCategoryId(''); setNewCategory(''); setShowNewCat(false)
      setTouched({ subject: false, message: false })
      onCreated?.()
    } catch {
      setError('Creazione ticket fallita')
    } finally {
      setSaving(false)
    }
  }

  if (loading) return (
    <div className="tickets-row body">
      Carico categorie…
    </div>
  )

  const subjectInvalid = touched.subject && !subject.trim()
  const messageInvalid = touched.message && !message.trim()
  const canSubmit = !saving && subject.trim() && message.trim()

  return (
    <form className="ticket-form card-surface" onSubmit={submit} noValidate>
      <div className="form-head">
        <h3>Nuovo ticket</h3>
        <p>Descrivi il problema nel dettaglio e scegli la categoria più adatta.</p>
      </div>

      {error && <div className="alert error">{error}</div>}

      <div className="grid-two">
        <div className={`form-row ${subjectInvalid ? 'is-invalid' : ''}`}>
          <label htmlFor="t-subject">Oggetto <span className="req">*</span></label>
          <input
            id="t-subject"
            type="text"
            value={subject}
            onChange={e=>setSubject(e.target.value)}
            onBlur={()=>setTouched(s=>({ ...s, subject: true }))}
            placeholder="Breve descrizione…"
            aria-invalid={subjectInvalid}
            autoComplete="off"
          />
          <small className="hint">Scrivi un titolo chiaro e conciso.</small>
        </div>

        <div className="form-row">
          <label htmlFor="t-category">Categoria</label>

          {!showNewCat && (
            <>
              <select
                id="t-category"
                value={categoryId}
                onChange={e=>setCategoryId(e.target.value)}
                disabled={isSupport ? false : false}
              >
                <option value="">— seleziona —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <small className="hint">
                Scegli una categoria esistente{isSupport ? ' oppure crea una nuova.' : '.'}
              </small>
            </>
          )}

          {isSupport && (
            <div className="inline-actions">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => setShowNewCat(v => !v)}
              >
                {showNewCat ? 'Usa categoria esistente' : 'Crea nuova categoria'}
              </button>
            </div>
          )}

          {isSupport && showNewCat && (
            <>
              <input
                type="text"
                value={newCategory}
                onChange={e=>setNewCategory(e.target.value)}
                placeholder="Es. Assistenza hardware"
              />
              <small className="hint">Se compilato, verrà creata e associata al ticket.</small>
            </>
          )}
        </div>
      </div>

      <div className={`form-row ${messageInvalid ? 'is-invalid' : ''}`}>
        <label htmlFor="t-message">Messaggio <span className="req">*</span></label>
        <textarea
          id="t-message"
          rows={8}
          value={message}
          onChange={e=>setMessage(e.target.value)}
          onBlur={()=>setTouched(s=>({ ...s, message: true }))}
          placeholder="Descrivi il problema, i passaggi per riprodurlo e gli eventuali errori mostrati…"
          aria-invalid={messageInvalid}
        />
        <small className="hint">Più dettagli = risoluzione più rapida.</small>
      </div>

      <div className="actions-row">
        <button className="btn btn-primary" type="submit" disabled={!canSubmit}>
          {saving ? 'Invio…' : 'Invia ticket'}
        </button>
        <button
          type="button"
          className="btn btn-outline"
          onClick={()=>{
            setSubject(''); setMessage(''); setCategoryId(''); setNewCategory(''); setShowNewCat(false)
            setTouched({ subject:false, message:false }); setError('')
          }}
          disabled={saving}
        >
          Pulisci
        </button>
      </div>
    </form>
  )
}
